<template>
  <div v-if="hasData || loading || showErrorTile">
    <template v-if="!showErrorTile">
      <Card
        v-for="(dataBlock, dbIdx) in databoxData"
        :key="dataBlock.title + dataBlock.subtitle + dbIdx"
        :loadingState="loading"
        :title="dataBlock.isFallback ? dataBlock.title : ''"
      >
        <template v-if="dataBlock.title !== ''" #content>
          <Spinner
            v-if="pdfLoading"
            :disableStyles="true"
            :isGlobal="false"
            :showOverlay="false"
          ></Spinner>
          <span v-if="dataBlock.isFallback" v-dompurify-html="dataBlock.content"> </span>
          <Collapser
            v-else
            id="shur.toggle-details"
            :collapserSubTitle="dataBlock.subtitle"
            :collapserTitle="dataBlock.title"
            :expanded="false"
            trackId="toggleDetails"
          >
            <template #collapserContent>
              <template v-if="!dataBlock.isFallback">
                <div v-for="(dataSection, idx) in dataBlock.datablock" :key="idx">
                  <DataBox :data="[dataSection]" @click:icon="clickEvent"></DataBox>
                </div>
              </template>
              <Alert v-else :size="AlertSize.SMALL" type="info">
                <template #main>
                  Aktuell können Ihre Vertragsdetails nicht angezeigt werden.
                </template>
              </Alert>
            </template>
          </Collapser>
        </template>
      </Card>
    </template>

    <!--    Error Tile-->
    <ErrorTile
      v-else-if="showErrorTile"
      :showFooter="false"
      :text="$t('shurDetails.errorContent')"
      :title="$t('shurDetails.errorTitle')"
    ></ErrorTile>

    <Modal
      :centeredText="false"
      :headerDivider="false"
      :maxWidth="680"
      :showCloseIcon="true"
      :showModal="showModalErrorPDF"
      @closeModal="closeModal"
    >
      <template #content>
        <ImageWrapper
          :breakpoints="[300, 300, 353, 353, 353]"
          imageName="Teaser_technischerFehler"
          imagePath="assets/images/Teaser_technischerFehler"
        />
        <h2 style="font-weight: 500; font-size: 30px">
          PDF Ansicht aktuell nicht verfügbar
        </h2>
        <div v-dompurify-html="$t('shurDetails.errorTitlePDF')"></div>
      </template>

      <template #footer>
        <CustomButton
          :buttonColor="ButtonColor.SECONDARY"
          :disabledState="false"
          @click="closeModal"
        >
          {{ $t('shurDetails.cancelButtonText') }}
        </CustomButton>
      </template>
    </Modal>

    <Modal
      :centeredText="false"
      :maxWidth="1070"
      :showCloseIcon="true"
      :showModal="showModalProgressiveScaleInfo"
      title="Progressionsstaffel"
      @closeModal="closeModal"
    >
      <template #content>
        <div
          v-dompurify-html="$t('shurDetails.sections.ACCIDENT.PROGRESSIVE_SCALE_INFO')"
        ></div>
      </template>
    </Modal>

    <Modal
      :centeredText="false"
      :maxWidth="1070"
      :showCloseIcon="true"
      :showModal="showModalDisabilityCovExistsInfo"
      title="Invaliditätshöchstleistung"
      @closeModal="closeModal"
    >
      <template #content>
        <div
          v-dompurify-html="
            $t('shurDetails.sections.ACCIDENT.DISABILITY_COV_EXISTS_INFO')
          "
        ></div>
      </template>
    </Modal>

    <Modal
      :centeredText="false"
      :maxWidth="1070"
      :showCloseIcon="true"
      :showModal="showModalInstantCoverage"
      title="Hinweis zu Ihrer Sofortschutzdeckung"
      @closeModal="closeModal"
    >
      <template #content>
        <div v-dompurify-html="$t('shurModal.instantCoverage')"></div>
      </template>
    </Modal>

    <Modal
      :centeredText="false"
      :maxWidth="1070"
      :showCloseIcon="true"
      :showModal="showModalSupplementaryCover"
      title="Hinweis zu Ihrer Ergänzungsdeckung"
      @closeModal="closeModal"
    >
      <template #content>
        <div v-dompurify-html="$t('shurModal.supplementaryCover')"></div>
      </template>
    </Modal>

    <Modal
      :centeredText="false"
      :maxWidth="1070"
      :showCloseIcon="true"
      :showModal="showModalDeductibleRS"
      title="Selbstbeteiligung je Versicherungsfall"
      @closeModal="closeModal"
    >
      <template #content>
        <div v-dompurify-html="$t('shurModal.deductibleRS')"></div>
      </template>
    </Modal>

    <Modal
      :centeredText="false"
      :maxWidth="1070"
      :showCloseIcon="true"
      :showModal="showModalPropertyRS"
      title="Immobilien-Rechtsschutz"
      @closeModal="closeModal"
    >
      <template #content>
        <div v-dompurify-html="$t('shurModal.propertyRS')"></div>
      </template>
    </Modal>

    <Modal
      :centeredText="false"
      :maxWidth="1070"
      :showCloseIcon="true"
      :showModal="showModalInsuredBenefitsInfo"
      title="Versicherte Leistungen & Ereignisse"
      @closeModal="closeModal"
    >
      <template #content>
        <div v-dompurify-html="$t('shurModal.insuredBenefitsTR')"></div>
      </template>
    </Modal>

    <!-- Pop-up für "Selbsthilfe bei Bauausführung" -->
    <Modal
      :centeredText="false"
      :maxWidth="1070"
      :showCloseIcon="true"
      :showModal="showModalSelfHelpConstruction"
      title="Selbsthilfe bei Bauausführung"
      @closeModal="closeModal"
    >
      <template v-slot:content>
        <div v-dompurify-html="$t('shurModal.selfHelpConstruction')"></div>
      </template>
    </Modal>

    <Modal
      :centeredText="false"
      :maxWidth="1070"
      :showCloseIcon="true"
      :showModal="showModalSelfHelpPlanning"
      title="Selbsthilfe bei der Planung"
      @closeModal="closeModal"
    >
      <template v-slot:content>
        <div v-dompurify-html="$t('shurModal.selfHelpPlanning')"></div>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, type Ref, ref } from 'vue'
import {
  Alert,
  AlertSize,
  ButtonColor,
  Card,
  Collapser,
  CustomButton,
  DataBox,
  Modal,
  Spinner
} from 'de-zurich-ui-library'
import ErrorTile from '@/components/common/ErrorTile.vue'
import {
  contractToDataboxData,
  type DataBoxWithHeader,
  isPrivatSchutz
} from '@/utils/shur/SHURDataboxData'
import { type Contract, ProductSubType, ProductType, type ShurContract } from 'shur-api'
import { useShurStore } from '@/stores/shurStore'
import ImageWrapper from '@/components/rahmen/ImageWrapper.vue'

const props = defineProps({
  policyId: {
    type: String,
    required: true
  },
  line: {
    type: String,
    required: true
  }
})

const hasData = ref(false)
const isBundle = ref(false)
const loading = ref(true)
const pdfLoading = ref(false)
const showErrorTile = ref(false)
const showModalDeductibleRS = ref(false)
const showModalDisabilityCovExistsInfo = ref(false)
const showModalErrorPDF = ref(false)
const showModalInstantCoverage = ref(false)
const showModalProgressiveScaleInfo = ref(false)
const showModalPropertyRS = ref(false)
const showModalSupplementaryCover = ref(false)
const showModalInsuredBenefitsInfo = ref(false)
const showModalSelfHelpPlanning = ref(false)
const showModalSelfHelpConstruction = ref(false)
const activationKeyPressed = ref(false)
const activationActionConsumed = ref(false)

const modalOpeners: Record<string, () => void> = {
  openModal_disabilityCovExistsInfo: () => {
    showModalDisabilityCovExistsInfo.value = true
  },
  openModal_instantCoverage: () => {
    showModalInstantCoverage.value = true
  },
  openModal_progressiveScaleInfo: () => {
    showModalProgressiveScaleInfo.value = true
  },
  openModal_supplementaryCover: () => {
    showModalSupplementaryCover.value = true
  },
  openModal_deductibleRS: () => {
    showModalDeductibleRS.value = true
  },
  openModal_propertyRS: () => {
    showModalPropertyRS.value = true
  },
  openModal_insuredBenefitsInfo: () => {
    showModalInsuredBenefitsInfo.value = true
  },
  openModal_selfHelpPlanningInfo: () => {
    showModalSelfHelpPlanning.value = true
  },
  openModal_selfHelpConstructionInfo: () => {
    showModalSelfHelpConstruction.value = true
  }
}

const databoxData: Ref<DataBoxWithHeader[]> = ref([
  {
    title: '',
    subtitle: '',
    datablock: [],
    isFallback: false
  }
])

onMounted(async () => {
  globalThis.addEventListener('keydown', onActivationKeydown, true)
  globalThis.addEventListener('keyup', onActivationKeyup, true)

  showErrorTile.value = false
  try {
    const shurContractForCheck = await useShurStore().getShurContractByPolicyId(
      props.policyId
    )
    const isPS = await isPrivatSchutz(shurContractForCheck)

    if (
      (!isPS && props.line !== 'transport') ||
      shurContractForCheck?.productSubType === 'VAULT_CONTENTS'
    ) {
      loading.value = false
      databoxData.value = []
      hasData.value = false
      return
    }

    let productSubType: ProductSubType | undefined
    let shurContract: ShurContract | null = null

    // Fetch contract to get productSubType for transport and building lines
    if (props.line === 'transport' || props.line === 'building') {
      shurContract = await useShurStore().getShurContractByPolicyId(props.policyId)
      productSubType = shurContract?.productSubType

      // For TRANSPORT/UNKNOWN, create a minimal contract structure without API call
      if (
        shurContract &&
        shurContract.contractBase &&
        shurContract.productType === ProductType.Transport &&
        shurContract.productSubType === 'UNKNOWN'
      ) {
        const minimalContract: Contract = {
          productType: ProductType.Transport,
          contractBase: shurContract.contractBase,
          trLine: [
            {
              productSubType: 'UNKNOWN',
              details: []
            }
          ],
          showFallback: false
        }
        await setData(minimalContract)
        return
      }
    }

    const shurContractDetails = await useShurStore().getShurContractDetailsByPolicyId(
      props.policyId,
      productSubType
    )
    if (!shurContractDetails) {
      showErrorTile.value = true
      return
    }
    await setData(shurContractDetails)
  } catch (e) {
    console.error('SHURPolicyDetails', e)
    showErrorTile.value = true
  } finally {
    loading.value = false
  }
})

onUnmounted(() => {
  globalThis.removeEventListener('keydown', onActivationKeydown, true)
  globalThis.removeEventListener('keyup', onActivationKeyup, true)
})

function onActivationKeydown(event: KeyboardEvent): void {
  activationKeyPressed.value = true
  if (!event.repeat) {
    activationActionConsumed.value = false
  }
}

function onActivationKeyup(_event: KeyboardEvent): void {
  activationKeyPressed.value = false
  activationActionConsumed.value = false
}

function clickEvent(href: string): void {
  if (activationKeyPressed.value) {
    if (activationActionConsumed.value) return
    activationActionConsumed.value = true
  }

  const openModal = modalOpeners[href]
  if (openModal) {
    openModal()
    return
  }

  // Global lock: while one PDF request is in flight, ignore additional PDF actions.
  if (pdfLoading.value) return

  const covConValues = decodeURIComponent(href.toString()).split('|')
  if (!covConValues[1] || !covConValues[2]) return

  pdfLoading.value = true

  void getCovCon(covConValues[1]!, covConValues[2] as ProductSubType, covConValues[3])
}

function closeModal(): void {
  showModalDeductibleRS.value = false
  showModalDisabilityCovExistsInfo.value = false
  showModalErrorPDF.value = false
  showModalInstantCoverage.value = false
  showModalProgressiveScaleInfo.value = false
  showModalPropertyRS.value = false
  showModalSupplementaryCover.value = false
  showModalInsuredBenefitsInfo.value = false
  showModalSelfHelpConstruction.value = false
  showModalSelfHelpPlanning.value = false
}

async function getCovCon(
  coverageConceptType: string,
  shurSubLine: ProductSubType,
  productVersion?: string
) {
  if (!coverageConceptType || coverageConceptType.length <= 3) {
    pdfLoading.value = false
    return
  }

  const cleanCoverageConceptType = coverageConceptType.substring(3)
  const legalPropertyLines: Array<ProductSubType> = [
    'LEGAL_PROPERTY_LANDLORD',
    'LEGAL_PROPERTY_TENANT',
    'LEGAL_PROPERTY_OWNER'
  ]
  const cleanShurSubLine = legalPropertyLines.includes(shurSubLine)
    ? ProductSubType.LegalProperty
    : shurSubLine

  // get URL split by /
  const currentUrlSplit = globalThis.location.href.toString().split('/')
  // Use only last part of above URL, then split this URL at # and only use first part
  const contractId = currentUrlSplit[currentUrlSplit.length - 1]!.split('#').at(0) ?? ''

  try {
    const resp = await useShurStore().getCoverageConceptPDFByContractId(
      contractId,
      cleanCoverageConceptType,
      cleanShurSubLine,
      productVersion
    )
    openPDFWithBlob(resp)
  } catch (err) {
    console.error('Error while fetching CoverageConcept', JSON.stringify(err))
    showModalErrorPDF.value = true
  } finally {
    pdfLoading.value = false
  }
}

function openPDFWithBlob(data: BlobPart): void {
  const blob: Blob = new Blob([data], { type: 'application/pdf' })
  const fileURL = globalThis.URL.createObjectURL(blob)
  const windowReference = globalThis.open()
  const fileName = 'deckungskonzept.pdf'

  if (windowReference) {
    windowReference.location.assign(fileURL)
    windowReference.onload = () => {
      windowReference.document.title = fileName
      globalThis.URL.revokeObjectURL(fileURL)
    }
  } else {
    const tempDownloadLink: HTMLAnchorElement = globalThis.document.createElement('a')
    tempDownloadLink.setAttribute('style', 'display: none;')
    tempDownloadLink.setAttribute('href', fileURL)
    tempDownloadLink.setAttribute('download', fileName)
    tempDownloadLink.onload = () => globalThis.URL.revokeObjectURL(fileURL)

    globalThis.document.body.appendChild(tempDownloadLink)
    tempDownloadLink.click()
    tempDownloadLink.remove()
  }
}

async function setData(contract: Contract): Promise<void> {
  if (contract) {
    isBundle.value = contract.productType == ProductType.Bundle

    databoxData.value = contractToDataboxData(contract, isBundle.value).filter(
      // remove emtpy Cards
      (entry) => entry.datablock.length > 0 || entry.isFallback
    )

    hasData.value = databoxData.value.length > 0
  } else {
    databoxData.value = []
    hasData.value = false
  }
}
</script>

<style lang="scss" scoped>
.is-styled .list__wrapper div.hoverable-icon.hoverable-icon-span {
  display: flex;
}
</style>
